import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from 'ui/lib/utils';

/**
 * Ported from gearfocus-v2's marketplace IconButton. Sizes are expressed in px
 * (not Tailwind rem utilities) because gearfocus-web sets `html { font-size: 10px }`,
 * which would otherwise render the design-system rem sizes at 62.5% scale.
 */
const iconButtonVariants = cva(
  'relative inline-flex cursor-pointer items-center justify-center rounded-[10px] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'size-[28px] [&_svg]:size-[14px]',
        md: 'size-[36px] [&_svg]:size-[18px]',
        lg: 'size-[44px] [&_svg]:size-[22px]',
        /** 32px circle (matches the header Avatar, `h-[3.2rem]` at the 10px rem
         * root) — glyph is 17.6px, 10% larger than the 16px default, per Bill's
         * 2026-08-21 request for a more visible wishlist/cart icon. */
        avatar: 'size-[32px] [&_svg]:size-[17.6px]',
      },
      styleVariant: {
        ghost:
          'bg-transparent text-gray-700 hover:bg-transparent hover:text-primary',
        outline:
          'border border-gray-300 bg-white text-gray-700 hover:border-primary hover:text-primary',
        // Barely-there circular outline — header wishlist/cart. Deliberately no
        // hover highlight: the border alone deepens a step, the icon stays put.
        circle:
          'rounded-full border border-gray-100 bg-white text-gray-700 hover:border-gray-300',
        overlay: 'bg-black/60 text-white backdrop-blur-sm hover:bg-black/70',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        styleVariant: 'ghost',
        active: true,
        className: 'bg-primary-light text-primary',
      },
      {
        styleVariant: 'outline',
        active: true,
        className: 'border-primary bg-primary-light text-primary',
      },
      {
        styleVariant: 'circle',
        active: true,
        className: 'border-primary bg-primary-light text-primary',
      },
      {
        styleVariant: 'overlay',
        active: true,
        className: 'bg-primary text-white',
      },
    ],
    defaultVariants: {
      size: 'md',
      styleVariant: 'ghost',
      active: false,
    },
  }
);

interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButtonVariants> {
  icon: LucideIcon;
  label: string;
  badge?: number | string;
}

const IconButton = ({
  icon: Icon,
  label,
  badge,
  size = 'md',
  styleVariant = 'ghost',
  active = false,
  className,
  type = 'button',
  ...props
}: IconButtonProps) => {
  const isBadgeVisible = badge !== undefined && badge !== null && badge !== '';
  const isBadgeNumericZero = typeof badge === 'number' && badge === 0;

  return (
    <button
      type={type}
      aria-label={label}
      aria-pressed={active ?? undefined}
      data-slot="icon-button"
      data-active={active}
      className={cn(iconButtonVariants({ size, styleVariant, active }), className)}
      {...props}
    >
      <Icon aria-hidden="true" className={cn(active && 'fill-current')} />
      {isBadgeVisible && !isBadgeNumericZero && (
        <span
          aria-hidden="true"
          className="absolute -top-[4px] -right-[4px] inline-flex size-[16px] min-w-[16px] items-center justify-center rounded-full bg-primary px-[4px] text-[10px] font-semibold leading-none text-white tabular-nums"
        >
          {badge}
        </span>
      )}
    </button>
  );
};

export { IconButton, iconButtonVariants };
export default IconButton;
